import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronDown, Loader2, Printer, WifiOff } from 'lucide-react';
import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Print a label, one of two ways.
 *
 * **Browser** opens the usual print dialog. It works from any PC with no setup
 * and prints to anything the PC can print to, including a DYMO. It is the
 * fallback and, until a printer is set up, the only option — so it is the
 * button's default action rather than something hidden in a menu.
 *
 * **A configured printer** sends the label to the shop's print agent, which
 * prints it with no dialog at all: straight to a Zebra over the network, or
 * through a Windows driver.
 *
 * The word throughout is "sent", not "printed". Neither path can tell whether
 * a label physically came out — the browser hands off to the OS, and raw TCP
 * has no acknowledgement at all. Saying "printed" would be a claim we cannot
 * back, and the one time it matters is the time the roll ran out.
 */

/** How long to follow a queued job before giving up on a verdict. */
const JOB_POLL_TIMEOUT_MS = 30_000;
const JOB_POLL_INTERVAL_MS = 1_000;

export default function PrintButton({
  blobUrl,
  parcelIds = [],
  mailLabelIds = [],
  disabled = false,
  className = '',
  label = 'Print',
}) {
  const [sending, setSending] = useState(false);
  const cancelled = useRef(false);

  useEffect(() => () => {
    cancelled.current = true;
  }, []);

  const { data: status } = useQuery({
    queryKey: ['print-status'],
    queryFn: () => api.printing.status(),
    // Whether the agent PC is awake changes minute to minute, and a stale
    // "online" here means someone presses Print and nothing happens.
    refetchInterval: 30_000,
    staleTime: 10_000,
  });

  const printers = status?.printers ?? [];
  const hasLabels = parcelIds.length > 0 || mailLabelIds.length > 0;

  const printInBrowser = () => {
    if (!blobUrl) return;

    // A hidden frame so the dialog targets the label at its own page size
    // rather than the surrounding app.
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    Object.assign(frame.style, {
      position: 'fixed',
      right: '0',
      bottom: '0',
      width: '0',
      height: '0',
      border: '0',
    });
    frame.src = blobUrl;

    frame.onload = () => {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
      // Removing it immediately cancels the dialog in some browsers before the
      // user has answered it.
      setTimeout(() => frame.remove(), 60_000);
    };

    document.body.appendChild(frame);
  };

  const sendToPrinter = async (printer) => {
    setSending(true);

    try {
      const job = await api.printing.print({
        printerId: printer.id,
        parcelIds,
        mailLabelIds,
      });

      const verdict = await followJob(job.id);

      if (cancelled.current) return;

      if (verdict.status === 'SENT') {
        toast.success(`Sent to ${printer.name}`);
      } else if (verdict.status === 'FAILED') {
        toast.error(verdict.error ?? `${printer.name} could not print that.`);
      } else {
        // Still queued after the timeout. Almost always the agent PC being
        // asleep — say that, rather than leaving a spinner going.
        toast.warning(
          `Still waiting for ${printer.name}. Check the print agent is running on the shop PC.`,
        );
      }
    } catch (error) {
      if (!cancelled.current) toast.error(error.message);
    } finally {
      if (!cancelled.current) setSending(false);
    }
  };

  // No printers set up: a plain button, no menu to open for a single choice.
  if (printers.length === 0) {
    return (
      <Button onClick={printInBrowser} disabled={disabled || !blobUrl} className={className}>
        <Printer className="w-4 h-4 mr-2" />
        {label}
      </Button>
    );
  }

  return (
    <div className={`flex ${className}`}>
      <Button
        onClick={printInBrowser}
        disabled={disabled || !blobUrl}
        className="flex-1 rounded-r-none"
      >
        <Printer className="w-4 h-4 mr-2" />
        {label}
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            disabled={disabled || sending}
            className="rounded-l-none border-l border-l-white/20 px-2"
            aria-label="Choose a printer"
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuItem onSelect={printInBrowser} disabled={!blobUrl}>
            <Printer className="w-4 h-4 mr-2" />
            Print dialog…
          </DropdownMenuItem>

          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-xs font-normal text-slate-500">
            Send straight to
          </DropdownMenuLabel>

          {printers.map((printer) => (
            <DropdownMenuItem
              key={printer.id}
              // A job for an offline agent queues and sits there. Better to
              // stop here and say why than to accept it and go quiet.
              disabled={!printer.online || !hasLabels || sending}
              onSelect={() => sendToPrinter(printer)}
              className="flex items-start gap-2"
            >
              {printer.online ? (
                <Printer className="w-4 h-4 mt-0.5 shrink-0" />
              ) : (
                <WifiOff className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
              )}
              <span className="min-w-0">
                <span className="block truncate">{printer.name}</span>
                <span className="block text-xs text-slate-500">
                  {!printer.online
                    ? `${printer.agentName ?? 'Agent'} is offline`
                    : printer.kind === 'ZEBRA_NETWORK'
                      ? 'Network Zebra'
                      : 'Windows printer'}
                </span>
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/**
 * Watch a queued job until the agent reports back.
 *
 * Polled rather than pushed. A print job resolves in a second or two and the
 * page is already open — a websocket for this would be more moving parts than
 * the thing it reports on.
 */
async function followJob(jobId) {
  const deadline = Date.now() + JOB_POLL_TIMEOUT_MS;

  for (;;) {
    const job = await api.printing.job(jobId);

    if (job.status === 'SENT' || job.status === 'FAILED' || job.status === 'CANCELLED') return job;
    if (Date.now() > deadline) return job;

    await new Promise((resolve) => setTimeout(resolve, JOB_POLL_INTERVAL_MS));
  }
}
